import React, { useState } from 'react'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { deleteSecret } from '../../../features/aws/awsSecretsManagerSlice'
import {
  deletePublicKey,
  fetchAllPublicKeys,
  fetchMcpPublicKeys,
} from '../integrationsSlice'
import { selectRootUser } from '../../../rootSlice'
import { isTeamOwner as checkIsTeamOwner } from '../isTeamOwner'

const MASKED = '••••••••••••••••••••••••••••••••'

const disconnectBtnClassName =
  'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-rose-200 hover:tw-bg-rose-50 hover:tw-text-rose-700 tw-transition-colors disabled:tw-opacity-50'

/** New flow stores a hash in key_value; legacy stores the plain key. Don't reveal hash. */
const isStoredHash = (value) => typeof value === 'string' && value.startsWith('aws:')

function accountLabelFromKey(keyObj) {
  const tags = keyObj?.tags
  if (Array.isArray(tags)) {
    const entry = tags.find((t) => String(t.key ?? t.Key ?? '').toLowerCase() === 'ownerid')
    const v = entry?.value ?? entry?.Value
    if (v) return String(v)
  }
  const kid = keyObj?.keyId || ''
  return kid.includes('--') ? kid.split('--')[0] : kid || '—'
}

function mcpKeyOwnedByCurrentUser(keyObj: { keyId?: string }, userId: unknown): boolean {
  const uid = userId != null && String(userId).trim() !== '' ? String(userId) : ''
  const kid = keyObj?.keyId != null ? String(keyObj.keyId) : ''
  return uid !== '' && kid.startsWith(`${uid}--`)
}

export default function McpKeyDisplay({
  ppKeys,
  ownerId = null,
  sharedAccountIds = [],
  canDisconnectAllTeamIntegrations = false,
}: {
  ppKeys: unknown[]
  ownerId?: string | number | null
  sharedAccountIds?: Array<string | number>
  canDisconnectAllTeamIntegrations?: boolean
}) {
  const dispatch = useDispatch()
  const user = useSelector(selectRootUser)
  const isTeamOwner = checkIsTeamOwner(user)
  const rows = Array.isArray(ppKeys) ? ppKeys.filter((k) => k?.privateKey || k?.keyId) : []
  const [visibleRows, setVisibleRows] = useState({})
  const [disconnectingKeyId, setDisconnectingKeyId] = useState<string | null>(null)

  if (rows.length === 0) return null

  const showActionsColumn = rows.some((keyObj) =>
    canDisconnectAllTeamIntegrations || mcpKeyOwnedByCurrentUser(keyObj as { keyId?: string }, user?.id)
  )

  const toggleRow = (idx) => {
    setVisibleRows((v) => ({ ...v, [idx]: !v[idx] }))
  }

  const handleDisconnect = async (keyId: string) => {
    if (!keyId) return
    if (
      !window.confirm(
        'Remove this MCP key? Clients using it will stop working until you generate a new key.'
      )
    ) {
      return
    }
    setDisconnectingKeyId(keyId)
    try {
      try {
        await dispatch(deleteSecret({ secretId: keyId })).unwrap()
      } catch {
        /* ignore — secret may already be gone */
      }
      try {
        await dispatch(deletePublicKey({ keyId })).unwrap()
      } catch {
        /* ignore */
      }
      if (ownerId != null && String(ownerId).trim() !== '') {
        await dispatch(fetchAllPublicKeys({ ownerId })).unwrap()
      }
      await dispatch(
        fetchMcpPublicKeys({
          ownerId,
          sharedAccountIds: sharedAccountIds || [],
          currentUserId: user?.id,
          isTeamOwner,
        })
      ).unwrap()
      toast.success('MCP key removed')
    } catch (err) {
      console.error(err)
      toast.error('Failed to remove MCP key')
    } finally {
      setDisconnectingKeyId(null)
    }
  }

  return (
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-w-full tw-text-sm tw-shadow-sm tw-overflow-x-auto">
      <table className="tw-table-auto tw-border-collapse tw-w-full">
        <thead className="tw-border-b tw-font-medium tw-text-slate-500 tw-bg-slate-50 tw-text-left">
          <tr>
            <th className="tw-px-2 tw-py-1">Account</th>
            <th className="tw-px-2 tw-py-1">MCP Key</th>
            <th className="tw-px-2 tw-py-1 tw-w-10" />
            {showActionsColumn ? <th className="tw-px-2 tw-py-1 tw-text-center">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((keyObj, idx) => {
            const keyValue = keyObj?.privateKey || ''
            const canReveal = !isStoredHash(keyValue)
            const visible = !!visibleRows[idx]
            const displayValue =
              canReveal && visible ? keyValue : canReveal ? MASKED : 'Stored securely'
            const showEye = canReveal
            const kid = keyObj?.keyId != null ? String(keyObj.keyId) : ''

            return (
              <tr key={keyObj.keyId || idx} className="tw-border-b tw-border-slate-100 last:tw-border-0">
                <td className="tw-px-2 tw-py-1 tw-font-mono tw-text-xs tw-break-all tw-text-slate-600 tw-align-top">
                  {accountLabelFromKey(keyObj)}
                </td>
                <td className="tw-px-2 tw-py-1 tw-font-mono tw-break-all tw-align-top">
                  <span className="tw-whitespace-nowrap">{displayValue}</span>
                </td>
                <td className="tw-px-2 tw-py-1 tw-text-slate-500 tw-align-top">
                  {showEye ? (
                    <button
                      type="button"
                      className="tw-cursor-pointer hover:tw-text-slate-900 tw-p-1 tw-rounded"
                      onClick={() => toggleRow(idx)}
                      aria-label={visible ? 'Hide key' : 'Show key'}
                      title={visible ? 'Hide key' : 'Show key'}
                    >
                      {visible ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  ) : null}
                </td>
                {showActionsColumn ? (
                  <td className="tw-px-2 tw-py-1 tw-text-center tw-align-top">
                    {kid &&
                    (canDisconnectAllTeamIntegrations ||
                      mcpKeyOwnedByCurrentUser(keyObj as { keyId?: string }, user?.id)) ? (
                      <button
                        type="button"
                        className={disconnectBtnClassName}
                        disabled={disconnectingKeyId === kid}
                        onClick={() => handleDisconnect(kid)}
                      >
                        {disconnectingKeyId === kid ? '…' : 'Disconnect'}
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
